import {
  getContractListData,
  propagateContractAdditions,
  propagateContractChanges,
} from '../api/contracts/mockData'
import { getOrderListData } from '../api/orders/mockData'
import { getOrderMasterServiceListData } from '../api/orderMasterServices/mockData'
import {
  getOrderMSServiceListData,
  propagateOrderMSServiceOrServicesAdditions,
  propagateOrderMSServicesChanges,
} from '../api/orderMSServices/mockData'
import {
  getOpportunityMSServiceListData,
  propagateOpportunityMSServiceOrServicesAdditions,
  propagateOpportunityMSServicesChanges,
} from '../api/opportunityMSServices/mockData'
import {
  getCompanyMSServiceListData,
  propagateCompanyMSServiceOrServicesAdditions,
  propagateCompanyMSServicesChanges,
} from '../api/companyMSServices/mockData'
import {
  getCompanyMasterServiceListData,
  propagateCompanyMasterServiceAdditions,
  propagateCompanyMasterServiceChanges,
} from '../api/companyMasterServices/mockData'
import type { TEntityName } from '~/types/models'

/**
 * Which mock list a row belongs to, resolved from its `entity` name.
 *
 * The per-collection `/actions` routes never needed this: the collection is in
 * the URL, so each route module closes over its own list function at
 * registration time. `POST /api/transactions` has no collection, so `action.entity`
 * is the only thing that says where a row goes — and there was no lookup from
 * that name to anything, anywhere in `api/msw`.
 *
 * 🔑 `storageKeyBase` IS NOT DECORATIVE, AND IT IS NOT DERIVABLE. Mock
 * persistence normally takes its key from the URL
 * (`getEntityStorageKeyBaseFromApiPath`), which is correct only while the URL
 * and the entity agree. On the collection-less route they cannot: one request
 * carries four entities and the path says none of them, so every list would be
 * written into the `transactions` slot and clobber it. Every write here therefore
 * passes `entityKeyBaseOverride` — the same escape hatch `api/msw/api/orders`
 * already uses for its cross-entity write.
 *
 * The key base is the lowercased plural URL segment (`ordermsservices`) while
 * the entity name is camelCase and singular (`orderMSService`). Lowercasing is
 * not enough and pluralising is not safe to guess, so both are written out.
 *
 * ⚠ REGISTER A NEW ENTITY HERE before sending actions for it — an unregistered
 * name is a 422, deliberately, rather than a silently skipped write. Same
 * convention as `cynqueModules/getEntityIdPrefix.ts`.
 */
export interface IEntityActionsRegistration {
  /** Returns the live shared array for this entity. */
  getEntityListDataFn: Function
  /** The localForage key base. See the note above — do not derive it. */
  storageKeyBase: TEntityName
  propagateAdditionsFn?: Function
  propagateChangesFn?: Function
}

export const ENTITY_ACTIONS_REGISTRY: Record<
  string,
  IEntityActionsRegistration
> = {
  contract: {
    getEntityListDataFn: getContractListData,
    storageKeyBase: 'contracts',
    propagateAdditionsFn: propagateContractAdditions,
    propagateChangesFn: propagateContractChanges,
  },
  order: {
    getEntityListDataFn: getOrderListData,
    storageKeyBase: 'orders',
  },
  orderMasterService: {
    getEntityListDataFn: getOrderMasterServiceListData,
    storageKeyBase: 'ordermasterservices',
  },
  orderMSService: {
    getEntityListDataFn: getOrderMSServiceListData,
    storageKeyBase: 'ordermsservices',
    propagateAdditionsFn: propagateOrderMSServiceOrServicesAdditions,
    propagateChangesFn: propagateOrderMSServicesChanges,
  },
  opportunityMSService: {
    getEntityListDataFn: getOpportunityMSServiceListData,
    storageKeyBase: 'opportunitymsservices',
    propagateAdditionsFn: propagateOpportunityMSServiceOrServicesAdditions,
    propagateChangesFn: propagateOpportunityMSServicesChanges,
  },
  companyMSService: {
    getEntityListDataFn: getCompanyMSServiceListData,
    storageKeyBase: 'companymsservices',
    propagateAdditionsFn: propagateCompanyMSServiceOrServicesAdditions,
    propagateChangesFn: propagateCompanyMSServicesChanges,
  },
  companyMasterService: {
    getEntityListDataFn: getCompanyMasterServiceListData,
    storageKeyBase: 'companymasterservices',
    // ⚠ `propagateCompanyMasterServiceAdditions` took a SINGLE row until this
    // registration existed; the /actions handler always passes an ARRAY, and
    // reading `.company.id` off one threw inside the write phase — rolling the
    // whole transaction back and failing every completion that creates a company
    // master service. It now normalises row-or-array, like its msService
    // sibling. See the note there before changing either.
    propagateAdditionsFn: propagateCompanyMasterServiceAdditions,
    propagateChangesFn: propagateCompanyMasterServiceChanges,
  },
}

/** The registration for `entityName`, or undefined if it is not registered. */
export function getEntityActionsRegistration(
  entityName: TEntityName
): IEntityActionsRegistration | undefined {
  return ENTITY_ACTIONS_REGISTRY[entityName]
}
